'use client';

import Icon from '@/components/ui/AppIcon';

interface DataUsageSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function DataUsageSection({ isExpanded, onToggle }: DataUsageSectionProps) {
  const usagePurposes = [
    {
      icon: 'HomeIcon',
      title: 'Reservation Processing',
      description: 'To confirm bookings, prepare accommodations, and manage your stay',
      optOut: false
    },
    {
      icon: 'SparklesIcon',
      title: 'Service Personalization',
      description: 'To tailor recommendations, remember preferences, and enhance your experience',
      optOut: true
    },
    {
      icon: 'EnvelopeIcon',
      title: 'Marketing Communications',
      description: 'To send promotional offers, newsletters, and updates about our services',
      optOut: true
    },
    {
      icon: 'BuildingOfficeIcon',
      title: 'Corporate Account Management',
      description: 'To manage corporate bookings, generate reports, and process invoices',
      optOut: false
    }
  ];

  return (
    <section id="data-usage" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="CogIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Data Usage</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              How We Use Your Information
            </h2>
            <p className="text-lg text-text-secondary">
              Your data is used exclusively to provide and improve our services. We never sell your personal information to third parties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {usagePurposes.map((purpose, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-card border border-border p-6 transition-all duration-300 hover:shadow-elevated"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={purpose.icon as any} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {purpose.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-3">
                      {purpose.description}
                    </p>
                    {purpose.optOut ? (
                      <div className="flex items-center space-x-2">
                        <Icon name="CheckCircleIcon" size={16} className="text-green-600" />
                        <span className="text-xs text-green-600 font-medium">Opt-out available</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Icon name="LockClosedIcon" size={16} className="text-text-tertiary" />
                        <span className="text-xs text-text-tertiary font-medium">Essential service</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onToggle}
            className="mt-8 w-full p-6 bg-white rounded-xl shadow-card border border-border hover:shadow-elevated transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-primary" />
                <span className="text-lg font-semibold text-text-primary">Manage Communication Preferences</span>
              </div>
              <Icon 
                name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                size={24} 
                className="text-text-tertiary" 
              />
            </div>
            {isExpanded && (
              <div className="mt-6 pt-6 border-t border-border text-left animate-scale-in">
                <p className="text-text-secondary mb-4">
                  You can opt out of marketing communications at any time by:
                </p>
                <ul className="space-y-2 text-text-secondary">
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span>Clicking the unsubscribe link in any promotional email</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span>Updating your preferences in your account settings</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span>Contacting our Data Protection Officer directly</span>
                  </li>
                </ul>
                <p className="text-text-secondary text-sm mt-4">
                  Note: You will continue to receive essential service communications (booking confirmations, account updates) even if you opt out of marketing.
                </p>
              </div>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}